import { alpha, createTheme } from '@mui/material'
import type {} from '@mui/x-date-pickers/themeAugmentation'
import { COLORS } from '../../variables'

declare module '@mui/material/styles' {
  interface TypographyVariants {
    typHeader: React.CSSProperties;
    "cerulean-14-semiBold": React.CSSProperties;
    "black-16-semiBold": React.CSSProperties;
    "black-14-medium": React.CSSProperties;
    "black-14-normal": React.CSSProperties;
    "black-14-light": React.CSSProperties;
    "pictonBlue-12-medium": React.CSSProperties;
    "pictonBlue-12-normal": React.CSSProperties;
    "black-12-light": React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    typHeader?: React.CSSProperties;
    "black-16-semiBold"?: React.CSSProperties;
    "cerulean-14-semiBold"?: React.CSSProperties;
    "black-14-medium"?: React.CSSProperties;
    "black-14-normal"?: React.CSSProperties;
    "black-14-light"?: React.CSSProperties;
    "pictonBlue-12-medium"?: React.CSSProperties;
    "pictonBlue-12-normal"?: React.CSSProperties;
    "black-12-light"?: React.CSSProperties;
  }

  interface Components {
    MuiPickersDay?: {
      styleOverrides?: {
        root?: React.CSSProperties | Record<string, unknown>
      }
    }
    MuiPickersCalendarHeader?: {
      styleOverrides?: {
        root?: React.CSSProperties | Record<string, unknown>
      }
    }
    MuiDateCalendar?: {
      styleOverrides?: {
        root?: React.CSSProperties | Record<string, unknown>
      }
    }
    MuiDayCalendar?: {
      styleOverrides?: {
        root?: React.CSSProperties | Record<string, unknown>
      }
    }
    MuiPickersYear?: {
      styleOverrides?: {
        root?: React.CSSProperties | Record<string, unknown>
      }
    }
    MuiPickersMonth?: {
      styleOverrides?: {
        root?: React.CSSProperties | Record<string, unknown>
      }
    }
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    follow: true;
    action: true;
    danger: true;
  }
}

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    primaryCard: true;
    primaryAdaptiveCard: true;
  }
}

declare module '@mui/material/Card' {
  interface CardPropsVariantOverrides {
    primaryCard: true; 
    primaryAdaptiveCard: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    typHeader: true;
    "black-16-semiBold": true;
    "cerulean-14-semiBold": true;
    "black-14-medium": true;
    "black-14-normal": true;
    "black-14-light": true;
    "pictonBlue-12-medium": true;
    "pictonBlue-12-normal": true;
    "black-12-light": true;
  }
}

export const PureLightTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1840,
    },
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          maxWidth: '1440px',
        },
      }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          background: COLORS.WHITE,
        }
      }
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: COLORS.CERULEAN,
            '& + .MuiSwitch-track': {
              backgroundColor: COLORS.CERULEAN,
              opacity: 0.5,
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          alignSelf: 'flex-start',
        },
        containedPrimary: {
          textTransform: 'none',
          borderRadius: 20,
          fontWeight: 400,
          fontSize: '16px',
          padding: '10px 30px',
          lineHeight: 1,
          background: COLORS.CATSKILL_WHITE,
          color: COLORS.BLACK,
          minWidth: 6,
          '&:hover': {
            background: COLORS.LINK_WATER,
          },
          '@media (hover: none)': {
            '&:hover': {
              background: COLORS.CATSKILL_WHITE,
            }
          },
          '& .MuiButton-startIcon': {
            marginLeft: 0,
          },
        },
        containedSecondary: ({theme}) => ({
          textTransform: 'none',
          borderRadius: 20,
          fontWeight: 500,
          fontSize: '14px',
          padding: '6px 16px',
          lineHeight: 1,
          gap: "8px",
          background: COLORS.WHITE_SMOKE,
          color: COLORS.SMOKEY_GREY,
          minWidth: 6,
          minHeight: "32px",
          '&:hover': {
            background: COLORS.SOFT_PEACH,
          },
          // '@media (hover: hover)': {
          //   '&:hover': {
          //     background: COLORS.WHITE_SMOKE,
          //   }
          // },
          '& .MuiButton-startIcon': {
            marginLeft: 0,
            marginRight: 0,
          },
          [theme.breakpoints.down("md")]: {
            padding: "6px 12px",
          },
          [theme.breakpoints.down("sm")]: {
            padding: 0,
            background: "transparent",
            // gap: "4px",
            borderRadius: "6px",
            minHeight: "26px",
            '& .MuiTouchRipple-root': {
              display: 'none',
            },
            '&:hover': {
              background: "transparent",
            },
          },
        }),
      },
      variants: [
        {
          props: { variant: "follow" },
          style: ({theme}) => ({
            background: COLORS.WHITE,
            border: `1px solid ${COLORS.CHARCOAL_GREY}`,
            color: COLORS.CHARCOAL_GREY,
            fontWeight: 500,
            fontSize: '14px',
            textTransform: 'none',
            borderRadius: "6px",
            minHeight: "32px",
            padding: "6px",
            lineHeight: 1,
            [theme.breakpoints.down("sm")]: {
              minHeight: "28px",
            },
          })
        },
        {
          props: { variant: "action" },
          style: ({theme}) => ({
            background: COLORS.WHITE,
            border: `1px solid ${COLORS.CERULEAN}`,
            color: COLORS.CHARCOAL_GREY,
            fontWeight: 500,
            fontSize: '14px',
            textTransform: 'none',
            borderRadius: "6px",
            minHeight: "32px",
            padding: "6px",
            lineHeight: 1,
            [theme.breakpoints.down("sm")]: {
              minHeight: "28px",
            },
          })
        },
        {
          props: { variant: "danger" },
          style: ({theme}) => ({
            background: COLORS.WHITE,
            border: `1px solid ${COLORS.CADMIUM_RED}`,
            color: COLORS.CADMIUM_RED,
            fontWeight: 500,
            fontSize: '14px',
            textTransform: 'none',
            borderRadius: "6px",
            minHeight: "32px",
            padding: "6px",
            lineHeight: 1,
            [theme.breakpoints.down("sm")]: {
              minHeight: "28px",
            },
          })
        },
      ]
    },
    // for textfield and autocomplete
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          // same styles
          borderRadius: "8px",
          border: `1px solid ${COLORS.LINK_WATER}`,
          backgroundColor: COLORS.WHITE,
          fontSize: 16,
          fontWeight: 400,
          "& fieldset": { border: "none" },
          // remove paddings top and bottom
          "& .MuiSelect-select": {
            paddingTop: 0,
            paddingBottom: 0,
          },
          '& .MuiInputAdornment-root .MuiIconButton-root .MuiSvgIcon-root': {
            color: COLORS.CERULEAN,
          },
          // "& input": {
          //   padding: 0,
          // },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          paddingTop: 0,
          paddingBottom: 0,
          fontSize: 16,
          fontWeight: 400,
          minHeight: 30,
          "& .MuiInputBase-input": {
            padding: "4px 8px",
            boxSizing: "border-box",
          },
        },
        listbox: {
          border: `1px solid ${COLORS.LINK_WATER}`,
          padding: 0,
          "& li": {
            fontSize: 16,
            fontWeight: 400,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          '&.Mui-error': {
            color: COLORS.CERULEAN,
          },
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          '&:before': {
            borderBottom: `1px solid ${COLORS.CERULEAN}`,
          },
          '&:hover:not(.Mui-disabled):before': {
            borderBottom: `2px solid ${COLORS.CERULEAN}`,
          },
          '&:after': {
            borderBottom: `2px solid ${COLORS.CERULEAN}`,
          },
          '&.Mui-error': {
            '&:before': {
              borderBottom: `1px solid ${COLORS.CERULEAN}`,
            },
            '&:after': {
              borderBottom: `2px solid ${COLORS.CERULEAN}`,
            },
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(COLORS.BLACK, 0.25),
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          display: 'none', 
        },
        root: {
          minHeight: 36,
        },
        flexContainer: { 
          gap: "20px",
        },
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 36, 
          height: 36,
          padding: "10px 20px",
          border: `1px solid ${COLORS.LINK_WATER}`,
          backgroundColor: COLORS.WHITE,
          borderRadius: "12px",
          fontSize: "14px",
          fontWeight: 600,
          lineHeight: "16px",
          color: COLORS.SMOKEY_GREY,
          textTransform: 'none',
          "&.Mui-selected": {
            color: COLORS.BLACK,
          },
          "&:hover": {
            color: COLORS.BLACK,
          },
        }
      }
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          '& .MuiStep-root.Mui-active .MuiStepContent-root': {
            borderColor: COLORS.CERULEAN, 
          },
          '& .MuiStep-root.Mui-completed .MuiStepContent-root': {
            borderColor: COLORS.CERULEAN, 
          },
        }
      }
    },
    MuiStepLabel: { 
      styleOverrides: { 
        label: { 
          color: COLORS.GRANITE,
          "&.Mui-active": { 
            color: COLORS.NICE_BLUE,
          }, 
          "&.Mui-completed": { 
            color: COLORS.BLACK,
          }, 
        }, 
      }, 
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: COLORS.GRANITE,
          "&.Mui-active": { 
            color: COLORS.CERULEAN,
          }, 
          "&.Mui-completed": { 
            color: COLORS.CERULEAN,
          }, 
        },
        text: { 
          fill: COLORS.WHITE,
          fontSize: '14px',
          fontWeight: 500,
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        line: {
          color: COLORS.GRANITE,
        },
        root: {
          "&.Mui-active .MuiStepConnector-line": {
            borderColor: COLORS.CERULEAN,
          },
          "&.Mui-completed .MuiStepConnector-line": {
            borderColor: COLORS.CERULEAN,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          paddingTop: 0,
          paddingBottom: 0,
          color: COLORS.CERULEAN,
          "&.Mui-checked": {
            color: COLORS.CERULEAN,
          },
        },
      }
    },
    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          fontSize: 14,
          fontWeight: 300,
        },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: {
          color: COLORS.CERULEAN,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: COLORS.CERULEAN,
          color: COLORS.WHITE,
          '& .MuiChip-deleteIcon': {
            color: COLORS.WHITE,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: "4px",
          marginLeft: "4px",
          color: COLORS.CERULEAN,
          "& .MuiSvgIcon-root": { 
            fontSize: 21 
          },
          "&.Mui-checked": {
            color: COLORS.CERULEAN,
          },
        },
      },
    },
    MuiCard: {
      variants: [
        {
          props: { variant: 'primaryCard' },
          style: ({theme}) => ({
            background: COLORS.WHITE,
            border: `1px solid ${COLORS.LINK_WATER}`,
            borderRadius: '12px',
            padding: "20px",
            [theme.breakpoints.down("md")]: {
              padding: "12px",
            },
            [theme.breakpoints.down("sm")]: {
              padding: 0,
            },
            
            msOverflowStyle: 'none', 
            scrollbarWidth: 'none', 
            '&::-webkit-scrollbar': {
              display: 'none'
            }
          })
        },
        {
          props: { variant: 'primaryAdaptiveCard' },
          style: ({theme}) => ({
            background: COLORS.WHITE,
            border: `1px solid ${COLORS.LINK_WATER}`,
            borderRadius: '12px',
            padding: "20px",
            [theme.breakpoints.down("md")]: {
              padding: "12px",
            },
            [theme.breakpoints.down("sm")]: {
              background: "transparent",
              border: "none",
              borderRadius: 0,
              padding: 0,
            },
            
            msOverflowStyle: 'none', 
            scrollbarWidth: 'none', 
            '&::-webkit-scrollbar': {
              display: 'none'
            }
          })
        },
      ]
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '0px',
          "&:last-child": {
            padding: '0px',
          }
        },
      }
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          '& .MuiPaginationItem-root': {
            background: COLORS.WHITE,
            color: COLORS.SMOKEY_GREY,
            borderRadius: 12,
            minWidth: '41px',
            height: '41px',
            border: `1px solid ${COLORS.LINK_WATER}`,
            '@media (max-width:450px)': {
              minWidth: '29px',
              height: '29px',
            }
          },
          '@media (hover: none)': {
            '& .MuiPaginationItem-root.Mui-selected': {
              background: COLORS.LINK_WATER,
            }
          },
          '& .MuiPaginationItem-root.Mui-selected': {
            backgroundColor: COLORS.LINK_WATER,
            color: COLORS.BLACK,
          },
          '& .MuiPaginationItem-ellipsis': {
            border: 'none',
            backgroundColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }
        },
      }
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: alpha(COLORS.BLACK, 0.54),
          '& .MuiBottomNavigationAction-icon': {
            color: 'inherit',
          },
          '& .MuiSvgIcon-root': {
            color: 'inherit',
          },
          '&.Mui-selected': {
            color: COLORS.NICE_BLUE,
          },
          '& .MuiBottomNavigationAction-label': {
            fontSize: '10px',
            color: COLORS.BLACK,
          },
          '&.Mui-selected .MuiBottomNavigationAction-label': {
            fontSize: '10px',
            fontWeight: 600,
            color: COLORS.NICE_BLUE,
          },
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: `${COLORS.CERULEAN} !important`,
            color: `${COLORS.WHITE} !important`,
          },
          '&.Mui-selected:hover': {
            backgroundColor: `${COLORS.CERULEAN} !important`,
          },
          '&.MuiPickersDay-today': {
            borderColor: COLORS.CERULEAN,
          },
          '&.Mui-focusVisible': {
            backgroundColor: alpha(COLORS.CERULEAN, 0.2),
          },
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        root: {
          '& .MuiIconButton-root': {
            color: COLORS.CERULEAN,
          },
          '& .MuiSvgIcon-root': {
            color: COLORS.CERULEAN,
          },
        },
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          '& .MuiPickersDay-root.Mui-selected': {
            backgroundColor: `${COLORS.CERULEAN} !important`,
            color: `${COLORS.WHITE} !important`,
          },
          '& .MuiPickersDay-root.Mui-selected:hover': {
            backgroundColor: `${COLORS.CERULEAN} !important`,
          },
          '& .MuiPickersDay-root[aria-selected="true"]': {
            backgroundColor: `${COLORS.CERULEAN} !important`,
            color: `${COLORS.WHITE} !important`,
          },
        },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        root: {
          '& .MuiPickersDay-root.Mui-selected': {
            backgroundColor: `${COLORS.CERULEAN} !important`,
            color: `${COLORS.WHITE} !important`,
          },
          '& .MuiPickersDay-root.Mui-focusVisible': {
            backgroundColor: alpha(COLORS.CERULEAN, 0.2),
          },
        },
      },
    },
    MuiPickersYear: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: COLORS.CERULEAN,
            color: COLORS.WHITE,
          },
          '&.Mui-selected:hover': {
            backgroundColor: COLORS.CERULEAN,
          },
          '&.Mui-focusVisible': {
            backgroundColor: alpha(COLORS.CERULEAN, 0.2),
          },
        },
      },
    },
    MuiPickersMonth: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: COLORS.CERULEAN,
            color: COLORS.WHITE,
          },
          '&.Mui-selected:hover': {
            backgroundColor: COLORS.CERULEAN,
          },
          '&.Mui-focusVisible': {
            backgroundColor: alpha(COLORS.CERULEAN, 0.2),
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        'html, body, #root': {
          width: '100%',
          height: '100%',
          background: COLORS.SNOW_DRIFT,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontSize: 14,
          fontWeight: 'bold',
          textTransform: 'uppercase',
        },
        colorDefault: {
          background: COLORS.GRANITE,
          color: COLORS.BLACK,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            background: COLORS.CATSKILL_WHITE,
          },
          '@media (hover: none)': {
            '&:hover': {
              background: 'transparent',
            }
          }
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          color: COLORS.BLACK,
        },
      },
    },

    MuiDivider: {
      styleOverrides: {
        root: {
          background: COLORS.LINK_WATER,
          border: 0,
          height: 1,
        },
        vertical: {
          height: 'auto',
          width: 1,

          '&.MuiDivider-flexItem.MuiDivider-fullWidth': {
            height: 'auto',
          },
          '&.MuiDivider-absolute.MuiDivider-fullWidth': {
            height: '100%',
          },
        },
        withChildren: {
          '&:before, &:after': {
            border: 0,
          },
        },
        wrapper: {
          background: COLORS.LINK_WATER,
          fontWeight: 'bold',
          height: 24,
          lineHeight: '24px',
          marginTop: -12,
          color: 'inherit',
          textTransform: 'uppercase',
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 0,

          '& .MuiListItem-button': {
            transition: 'all .2s',

            '& > .MuiSvgIcon-root': {
              minWidth: 34,
            },

            '& .MuiTouchRipple-root': {
              opacity: 0.2,
            },
          },
          '& .MuiListItem-root.MuiButtonBase-root.Mui-selected': {
            //backgroundColor: colors.alpha.black[10],
          },
        },
        padding: {
          padding: '12px',

          '& .MuiListItem-button': {
            borderRadius: 6,
            margin: '1px 0',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${COLORS.LINK_WATER}`,
          background: COLORS.SNOW_DRIFT,
        },
        list: {
          '& .MuiMenuItem-root.MuiButtonBase-root': {
            transition: 'all .2s',
            background: "transparent",
            color: COLORS.BLACK,

            '& .MuiTouchRipple-root': {
              opacity: 0.2,
            },

            '&:hover, &:active, &.active, &.Mui-selected': {
              background: COLORS.LINK_WATER,
            },
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.MuiButtonBase-root': {
            color: COLORS.BLACK,

            '&:hover, &:active, &.active, &.Mui-selected': {
              color: COLORS.BLACK,
            },
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '0 !important',
          padding: '0 !important',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: COLORS.BLACK,
          padding: '8px 16px',
          fontSize: 13,
        },
        arrow: {
          color: COLORS.BLACK,
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          h1: 'h1',
          h2: 'h2',
          h3: 'h3',
          h4: 'h4',
          h5: 'h5',
          h6: 'h6',
          subtitle1: 'div',
          subtitle2: 'div',
          body1: 'div',
          body2: 'div',
        },
      },
      variants: [
        {
          props: { variant: 'typHeader' },
          style: {
            fontSize: '14px',
            fontWeight: 600,
            color: COLORS.GRANITE,
            '&:hover': {
              color: COLORS.BLACK,
            },
          },
        },
      ],
      styleOverrides: {
        gutterBottom: {
          marginBottom: 4,
        },
        paragraph: {
          fontSize: 17,
          lineHeight: 1.7,
        },
      },
    },
  },
  shape: {
    borderRadius: 6,
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"',
    ].join(','),
    fontSize: 14,
    htmlFontSize: 15,
    h1: {
      fontWeight: 500,
      fontSize: 24,
      color: COLORS.BLACK,
    },
    h2: {
      fontWeight: 500,
      fontSize: 20,
      color: COLORS.BLACK,
    },
    h3: {
      fontWeight: 600,
      fontSize: 16,
      lineHeight: '20px',
      color: COLORS.BLACK,
    },
    h4: {
      fontWeight: 500,
      fontSize: 14,
      lineHeight: '18px',
      color: COLORS.BLACK,
    },
    h5: {
      fontWeight: 700,
      fontSize: 14,
    },
    h6: {
      fontSize: 15,
    },

    "black-16-semiBold": {
      color: COLORS.BLACK,
      fontSize: 16,
      fontWeight: 600,
    },
    "cerulean-14-semiBold": {
      color: COLORS.CERULEAN,
      fontSize: 14,
      fontWeight: 600,
    },
    "black-14-medium": {
      color: COLORS.BLACK,
      fontSize: 14,
      fontWeight: 500,
    },
    "black-14-normal": {
      color: COLORS.BLACK,
      fontSize: 14,
      fontWeight: 400,
    },
    "black-14-light": {
      color: COLORS.BLACK,
      fontSize: 14,
      fontWeight: 300,
    },
    "pictonBlue-12-medium": {
      color: COLORS.PICTON_BLUE,
      fontSize: 12,
      fontWeight: 500,
    },
    "pictonBlue-12-normal": {
      color: COLORS.PICTON_BLUE,
      fontSize: 12,
      fontWeight: 400,
    },
    "black-12-light": {
      color: COLORS.BLACK,
      fontSize: 12,
      fontWeight: 300,
    },

    body1: {
      fontWeight: 300,
      fontSize: 16,
      lineHeight: '20px',
      color: COLORS.BLACK,
    },
    body2: {
      fontWeight: 400,
      fontSize: 14,
      lineHeight: '18px',
      color: COLORS.BLACK,
    },
    // button: {
    //   fontSize: 14,
    //   fontWeight: 700,
    // },
    caption: {
      fontSize: 13,
      textTransform: 'uppercase',
      //color: colors.alpha.black[50],
    },
    subtitle1: {
      fontSize: 14,
      //color: colors.alpha.black[70],
    },
    subtitle2: {
      fontWeight: 400,
      fontSize: 15,
      //color: colors.alpha.black[70],
    },
    overline: {
      fontSize: 13,
      fontWeight: 700,
      textTransform: 'uppercase',
    },
  },
  shadows: [
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
    'none',
  ],
})
